import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import config from '../config';

const TrueLayerCallback: React.FC = () => {
  const { palette } = useTheme();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Connecting your bank via TrueLayer...');
  const isProcessing = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (isProcessing.current) return;
      isProcessing.current = true;

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          throw new Error(`TrueLayer error: ${error}`);
        }
        if (!code || !state) {
          throw new Error('Missing authorization code or state parameter');
        }

        const token = localStorage.getItem('auth_token');
        if (!token) {
          throw new Error('Please log in first');
        }

        const response = await fetch(`${config.apiUrl}/api/auth/truelayer/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            code,
            state,
            redirect_uri: `${window.location.origin}/auth/truelayer/callback`,
          }),
        });

        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setMessage('Bank connected successfully via TrueLayer');
          window.opener?.postMessage({ type: 'truelayer-auth-complete', success: true }, window.location.origin);

          const isNewTab = window.history.length === 1 || !window.opener;
          if (isNewTab) {
            setTimeout(() => { window.location.href = window.location.origin; }, 3000);
          } else {
            setTimeout(() => { window.close(); }, 3000);
          }
        } else {
          throw new Error(data.error || 'Failed to complete authentication');
        }
      } catch (err) {
        console.error('TrueLayer callback error:', err);
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Authentication failed');
        window.opener?.postMessage({ type: 'truelayer-auth-complete', success: false }, window.location.origin);

        const isNewTab = window.history.length === 1 || !window.opener;
        setTimeout(() => {
          if (isNewTab) {
            window.location.href = window.location.origin;
          } else {
            window.close();
          }
        }, 3000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: palette.tokens.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: 420, background: palette.tokens.white, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>
          {status === 'processing' && '⏳'}
          {status === 'success' && '✅'}
          {status === 'error' && '❌'}
        </div>
        <h2 style={{ fontWeight: 700, color: palette.tokens.ink, marginBottom: 8 }}>
          {status === 'processing' && 'Connecting your bank'}
          {status === 'success' && 'Bank connected'}
          {status === 'error' && 'Connection failed'}
        </h2>
        <p style={{ color: palette.tokens.muted }}>{message}</p>
        <p style={{ color: palette.tokens.mutedSoft, fontSize: 13, marginTop: 16 }}>
          {status === 'processing' ? 'Please wait...' : 'You will be redirected back to the app shortly'}
        </p>
      </div>
    </div>
  );
};

export default TrueLayerCallback;
